import {
  Button,
  Fieldset,
  Group,
  Modal,
  Stack,
  type ModalProps,
  type StackProps,
} from "@mantine/core";
import type { FormEventHandler, ReactNode } from "react";

type AdminFormProps = {
  readonly children: ReactNode;
  readonly onSubmit?: FormEventHandler<HTMLFormElement>;
  /** 表单主体间距，默认 md（与弹层表单一致） */
  readonly gap?: StackProps["gap"];
};

/**
 * 表单外壳：统一 Stack 间距；提交走原生 form 以便 Enter 触发 submit。
 */
export function AdminForm(props: AdminFormProps) {
  const { children, onSubmit, gap = "md" } = props;
  return (
    <form onSubmit={onSubmit} className="admin-form" noValidate>
      <Stack gap={gap}>{children}</Stack>
    </form>
  );
}

type AdminFormSectionProps = {
  readonly title: string;
  readonly children: ReactNode;
};

/** 分区：Fieldset + 内部 sm 间距（与奖品/支付等大表单一致） */
export function AdminFormSection(props: AdminFormSectionProps) {
  const { title, children } = props;
  return (
    <Fieldset legend={title} className="admin-form-section">
      <Stack gap="sm">{children}</Stack>
    </Fieldset>
  );
}

type AdminFormActionsProps = {
  readonly children: ReactNode;
};

/** 底部操作区：右对齐取消/提交 */
export function AdminFormActions(props: AdminFormActionsProps) {
  const { children } = props;
  return (
    <Group justify="flex-end" gap="sm" mt="xs" className="admin-form-actions" wrap="wrap">
      {children}
    </Group>
  );
}

type AdminFormModalProps = {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly title: ReactNode;
  readonly children: ReactNode;
  readonly size?: ModalProps["size"];
} & Omit<ModalProps, "opened" | "onClose" | "title" | "children" | "size">;

/**
 * 表单弹窗：继承 theme Modal 默认（居中、keepMounted=false、标题字重），
 * 只补 size 语义默认 md。
 */
export function AdminFormModal(props: AdminFormModalProps) {
  const { opened, onClose, title, children, size = "md", ...rest } = props;
  return (
    <Modal opened={opened} onClose={onClose} title={title} size={size} {...rest}>
      {children}
    </Modal>
  );
}

type CancelSubmitProps = {
  readonly onCancel: () => void;
  readonly submitLabel?: string;
  readonly cancelLabel?: string;
  readonly loading?: boolean;
  readonly submitDisabled?: boolean;
};

/** 最常见的「取消 + 提交」按钮对 */
export function AdminFormCancelSubmit(props: CancelSubmitProps) {
  const {
    onCancel,
    submitLabel = "保存",
    cancelLabel = "取消",
    loading = false,
    submitDisabled = false,
  } = props;
  return (
    <AdminFormActions>
      <Button variant="default" type="button" onClick={onCancel}>
        {cancelLabel}
      </Button>
      <Button type="submit" loading={loading} disabled={submitDisabled}>
        {submitLabel}
      </Button>
    </AdminFormActions>
  );
}
